import { GoogleGenerativeAI } from "@google/generative-ai";

const getApiKey = () => {
  const apiKey = process.env.REACT_APP_GEMINI_API_KEY || process.env.GEMINI_API_KEY;
  if (!apiKey) {
    throw new Error("Gemini API key is not configured. Set REACT_APP_GEMINI_API_KEY in your environment.");
  }
  return apiKey;
};

const MODEL_ID = process.env.REACT_APP_GEMINI_MODEL_ID || "gemini-2.5-flash";

const DEFAULT_PROMPT = `You are an OCR assistant. Read the provided document image and extract any text into clean key: value pairs.
- Use lowercase keys without spaces (e.g., name, course, admnno, year).
- Preserve important separators inside values such as colons or hyphens (e.g., 2023:2027).
- Return each key on a new line in the format key: value.
- If a key repeats, keep the first occurrence.
- If nothing meaningful is found, respond with an empty string.`;

let cachedModel = null;
let cachedKey = null;
let cachedModelId = null;

const getModel = () => {
  const apiKey = getApiKey();

  if (cachedModel && cachedKey === apiKey && cachedModelId === MODEL_ID) {
    return cachedModel;
  }

  const genAI = new GoogleGenerativeAI(apiKey);
  cachedModel = genAI.getGenerativeModel({ model: MODEL_ID });
  cachedKey = apiKey;
  cachedModelId = MODEL_ID;
  return cachedModel;
};

const readBlobAsBase64 = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = typeof reader.result === "string" ? reader.result : "";
      const [, base64 = ""] = result.split(",");
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error || new Error("Failed to read image blob"));
    reader.readAsDataURL(blob);
  });

const normalizeImageInput = async (imageInput) => {
  if (!imageInput) {
    throw new Error("No image provided for AI OCR");
  }

  if (typeof imageInput === "string") {
    const trimmed = imageInput.trim();
    if (!trimmed) throw new Error("Image input string is empty");

    if (trimmed.startsWith("data:")) {
      const [header, base64] = trimmed.split(",");
      const mimeMatch = header.match(/data:([^;]+)/);
      return {
        base64: base64 || "",
        mimeType: mimeMatch?.[1] || "image/jpeg",
      };
    }

    throw new Error("Only data URLs are supported for string inputs in AI OCR");
  }

  if (typeof Blob !== "undefined" && imageInput instanceof Blob) {
    const base64 = await readBlobAsBase64(imageInput);
    return {
      base64,
      mimeType: imageInput.type || "image/jpeg",
    };
  }

  throw new Error("Unsupported image input type for AI OCR");
};

export const extractAiTextFromImage = async (
  imageInput,
  prompt = DEFAULT_PROMPT
) => {
  const model = getModel();
  const { base64, mimeType } = await normalizeImageInput(imageInput);

  if (!base64) {
    throw new Error("Unable to read image content for AI OCR");
  }

  const imagePart = {
    inlineData: {
      data: base64,
      mimeType,
    },
  };

  const result = await model.generateContent([
    { text: prompt },
    imagePart,
  ]);

  const text = result?.response?.text?.();
  return (text || "").trim();
};

export default extractAiTextFromImage;

export const logAvailableModels = async () => {
  const genAI = new GoogleGenerativeAI(getApiKey());
  const response = await genAI.listModels({ pageSize: 50 });
  console.log(response.models?.map((model) => ({ name: model.name, supportedMethods: model.supportedGenerationMethods })));
};
